import type { BodyShape } from '@/types/quiz'

export interface BodyShapeImage {
  /** Path relative to site root. Lives in /public/shapes/*.webp — Vite copies it to dist as-is. */
  url: string
  alt: string
  /** Intrinsic width/height used for aspect ratio + CLS-safe rendering. */
  width: number
  height: number
}

export interface BodyShapeOption {
  id: BodyShape
  /** Short display label shown above the description. */
  label: string
  /** Plain-language description verbatim from the brand spec. */
  description: string
  /** Parenthetical shorthand for the shape. */
  shorthand: string
  /** Real imagery (swap by replacing the file at /public/shapes/<id>.webp). */
  image: BodyShapeImage
}

/**
 * The 4 body-shape options on Screen 3. Copy verbatim from the brand spec.
 */
export const BODY_SHAPES: readonly BodyShapeOption[] = [
  {
    id: 'pear',
    label: 'Narrow waist, fuller hips and thighs.',
    shorthand: 'Pear / Hourglass',
    description: 'Your waist is significantly narrower than your hips and bum, and you carry fullness through your thighs.',
    image: { url: '/shapes/pear.webp', alt: 'Pear / Hourglass body shape', width: 1200, height: 1500 },
  },
  {
    id: 'subtle_curve',
    label: 'Softly curved – defined, but not dramatic.',
    shorthand: 'Subtle Curve',
    description: 'You have gentle shaping through a defined waist and wider hip, but only a slight difference between them.',
    image: { url: '/shapes/subtle-curve.webp', alt: 'Subtle Curve body shape', width: 1200, height: 1500 },
  },
  {
    id: 'apple',
    label: 'Fullest through the middle, slimmer legs.',
    shorthand: 'Apple / Inverted',
    description: 'Your mid-section is the fullest part of your body, with slimmer thighs and narrower hips.',
    image: { url: '/shapes/apple.webp', alt: 'Apple / Inverted Triangle body shape', width: 1200, height: 1500 },
  },
  {
    id: 'rectangle',
    label: 'Straight up and down – similar width.',
    shorthand: 'Rectangle',
    description: 'Your waist and hips measure roughly the same, with minimal difference between them.',
    image: { url: '/shapes/rectangle.webp', alt: 'Rectangle body shape', width: 1200, height: 1500 },
  },
] as const
